import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Plus, Send, Mail, Key, Monitor, Calendar, Clock, FileText, ListChecks, ClipboardCheck, Search, Copy, MessageCircle, Camera, Trash2, CheckCircle2, AlertTriangle, X } from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import { parseDelivered } from "@/lib/deliveredFormat";

const TABS = ["Todos", "Pendientes", "En Proceso", "Resueltos"];
const STATUS_MAP = { Pendientes: "pendiente", "En Proceso": "en proceso", Resueltos: "resuelto" };
const STATUS_STYLE = {
  resuelto: { border: "border-l-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  "en proceso": { border: "border-l-blue-500", badge: "bg-blue-50 text-blue-700 border-blue-200", icon: Clock },
  pendiente: { border: "border-l-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
};
const copyText = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copiado"));
const WARRANTY_DAYS_MONTHLY = 25;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export default function Reportes() {
  const { logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [form, setForm] = useState({ correo_cuenta: "", password_cuenta: "", plataforma: "", fecha_compra: "", descripcion: "", imagen_url: "" });
  const [reports, setReports] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [tab, setTab] = useState("Todos");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [repStats, setRepStats] = useState(null);
  const imgInputRef = useRef(null);
  const esTop10 = myRank !== null && myRank <= 10;

  const load = () => api.get("/my/reports").then((r) => setReports(r.data)).catch(() => {});
  const loadStats = () => api.get("/my/reports/stats").then((r) => setRepStats(r.data)).catch(() => {});
  const loadPurchases = () => api.get("/my/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  const loadRank = () => api.get("/leaderboard").then((r) => setMyRank(r.data.my_rank)).catch(() => {});
  useEffect(() => { load(); loadStats(); loadPurchases(); loadRank(); }, []);
  useEffect(() => {
    if (!lightboxImg) return;
    const onKey = (e) => { if (e.key === "Escape") setLightboxImg(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxImg]);

  const counts = useMemo(() => repStats ? {
    total: repStats.total,
    pendiente: repStats.pendiente,
    "en proceso": repStats.proceso,
    resuelto: repStats.resuelto,
  } : {
    total: reports.length,
    pendiente: reports.filter((r) => r.status === "pendiente").length,
    "en proceso": reports.filter((r) => r.status === "en proceso").length,
    resuelto: reports.filter((r) => r.status === "resuelto").length,
  }, [reports, repStats]);

  // Cuentas realmente compradas por este cliente -- elegir de aquí (en vez de
  // escribir la plataforma y la fecha a mano) es lo que permite calcular la
  // garantía real, ya que una fecha escrita a mano se podía inventar.
  const misCompras = useMemo(() => {
    const list = [];
    purchases.forEach((s) => {
      (s.items || []).forEach((it) => {
        const duracionDias = it.expires_at ? Math.round((new Date(it.expires_at) - new Date(s.created_at)) / 86400000) : null;
        const esMensual = duracionDias !== null && duracionDias <= 31;
        const diasPasados = Math.floor((Date.now() - new Date(s.created_at)) / 86400000);
        const fueraDeGarantia = esMensual && diasPasados > WARRANTY_DAYS_MONTHLY;
        // Las cuentas repuestas por el admin (o ganadas en la ruleta de premios)
        // se guardan como "Reemplazo: <plataforma>" / "Premio Ruleta: <plataforma>"
        // (así se distinguen en el dashboard del admin) -- pero para que el cliente
        // pueda encontrarlas y reportarlas de nuevo, acá deben agruparse bajo la
        // plataforma real, no bajo ese nombre.
        const REPLACEMENT_PREFIXES = ["Reemplazo: ", "Premio Ruleta: "];
        const matchedPrefix = REPLACEMENT_PREFIXES.find((pre) => it.product_name?.startsWith(pre));
        const productName = matchedPrefix ? it.product_name.slice(matchedPrefix.length) : it.product_name;
        (it.delivered && it.delivered.length ? it.delivered : [""]).forEach((d, di) => {
          const p = d ? parseDelivered(d) : {};
          list.push({
            key: `${s.id}-${it.product_id}-${di}`,
            sale_id: s.id, product_id: it.product_id, product_name: productName,
            correo: p.texto || p.correo || d, password: p.contrasena || "", perfil: p.perfil || null,
            fecha: s.created_at, esMensual, fueraDeGarantia, diasPasados,
          });
        });
      });
    });
    return list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [purchases]);

  // Solo se listan las plataformas que este cliente compró de verdad -- si
  // pudiera elegir cualquiera del catálogo (ej. "X Completa" sin haberla
  // comprado, solo "X Perfil"), un reporte deshonesto podía cobrar el precio
  // de la variante más cara sin haberla pagado.
  const misPlataformas = useMemo(() => {
    const seen = new Set();
    const list = [];
    misCompras.forEach((c) => { if (!seen.has(c.product_name)) { seen.add(c.product_name); list.push(c.product_name); } });
    return list;
  }, [misCompras]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [plataformaOtro, setPlataformaOtro] = useState(false);
  const [compraSel, setCompraSel] = useState(null); // item elegido de misCompras, o null si es "otro"

  // Dos formas de identificar la cuenta, y cualquiera rellena todo lo demás:
  // 1) elegir la plataforma primero -> se listan solo los correos de esa
  //    plataforma; 2) escribir el correo directo -> busca en todas.
  const cuentaSugerencias = useMemo(() => {
    if (compraSel || plataformaOtro) return [];
    const needle = form.correo_cuenta.trim().toLowerCase();
    if (form.plataforma) {
      const base = misCompras.filter((c) => c.product_name === form.plataforma);
      return needle ? base.filter((c) => c.correo.toLowerCase().includes(needle)) : base;
    }
    if (!needle) return [];
    return misCompras.filter((c) => c.correo.toLowerCase().includes(needle)).slice(0, 8);
  }, [misCompras, form.correo_cuenta, form.plataforma, compraSel, plataformaOtro]);

  const pickCompra = (c) => {
    setCompraSel(c); setPlataformaOtro(false);
    setForm((s) => ({ ...s, plataforma: c.product_name, correo_cuenta: c.correo, password_cuenta: c.password, fecha_compra: c.fecha.slice(0, 10) }));
  };
  const clearCompra = () => {
    setCompraSel(null);
    setForm((s) => ({ ...s, fecha_compra: "" }));
  };
  const onPlataformaSelect = (e) => {
    const val = e.target.value;
    if (val === "__otro__") { activarModoOtro(); return; }
    setCompraSel(null);
    setForm((s) => ({ ...s, plataforma: val, correo_cuenta: "", password_cuenta: "", fecha_compra: "" }));
  };
  const activarModoOtro = () => {
    setCompraSel(null); setPlataformaOtro(true);
    setForm((s) => ({ ...s, plataforma: "", fecha_compra: "" }));
  };
  const backFromOtro = () => {
    setPlataformaOtro(false);
    setForm((s) => ({ ...s, plataforma: "" }));
  };

  const uploadImg = async (f) => {
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      return toast.error("Solo se aceptan imágenes PNG, JPG o WEBP");
    }
    if (f.size > MAX_IMAGE_BYTES) {
      return toast.error("La imagen debe pesar menos de 10 MB");
    }
    const fd = new FormData(); fd.append("file", f);
    setUploadingImg(true);
    try {
      const { data } = await api.post("/reports/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, imagen_url: data.url }));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploadingImg(false);
    }
  };
  const onImgFile = (e) => uploadImg(e.target.files?.[0]);
  const onDescPaste = (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    let file = cd.files && cd.files.length > 0 ? Array.from(cd.files).find((f) => f.type.startsWith("image/")) : null;
    if (!file) {
      const item = Array.from(cd.items || []).find((it) => it.type.startsWith("image/"));
      file = item ? item.getAsFile() : null;
    }
    if (file) { e.preventDefault(); uploadImg(file); }
  };

  const submit = async () => {
    if (!form.plataforma.trim()) return toast.error("Selecciona la cuenta que estás reportando");
    // Fuera del modo "otro" (legado), el reporte tiene que quedar ligado a una
    // compra real -- así el precio del reembolso sale de lo que de verdad
    // pagó (product_id), no de lo que escribió/eligió como plataforma.
    if (!compraSel && !plataformaOtro) return toast.error("Elige la cuenta exacta de tu historial de compras (o usa \"Otro\" si es de antes del cambio de web)");
    if (form.descripcion.trim().length < 10) return toast.error("La descripción debe tener mínimo 10 caracteres");
    if (!form.imagen_url && !esTop10) return toast.error("Adjunta una foto del problema");
    if (compraSel?.fueraDeGarantia) return toast.error(`Esta cuenta es de 1 mes y ya pasó los ${WARRANTY_DAYS_MONTHLY} días de garantía.`);
    setBusy(true);
    try {
      await api.post("/reports", { ...form, sale_id: compraSel?.sale_id || null, product_id: compraSel?.product_id || null, perfil: compraSel?.perfil || null });
      toast.success("Reporte enviado correctamente");
      setForm({ correo_cuenta: "", password_cuenta: "", plataforma: "", fecha_compra: "", descripcion: "", imagen_url: "" });
      setCompraSel(null); setPlataformaOtro(false);
      load(); loadStats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const filtered = reports.filter((r) => {
    const okTab = tab === "Todos" || r.status === STATUS_MAP[tab];
    const okQ = !q || (r.correo_cuenta || "").toLowerCase().includes(q.toLowerCase()) || (r.plataforma || "").toLowerCase().includes(q.toLowerCase());
    return okTab && okQ;
  });

  const stat = [
    { n: counts.total, l: "Total", icon: ListChecks, c: "text-primary", bg: "bg-primary/10" },
    { n: counts.pendiente, l: "Pendientes", icon: AlertTriangle, c: "text-amber-600", bg: "bg-amber-50" },
    { n: counts["en proceso"], l: "En Proceso", icon: Clock, c: "text-blue-600", bg: "bg-blue-50" },
    { n: counts.resuelto, l: "Resueltos", icon: CheckCircle2, c: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="max-w-5xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3"><TenantBrand tenant={tenant} imgClassName="h-10 w-auto object-contain" textClassName="font-head text-lg font-extrabold uppercase tracking-tight text-current" /><span className="font-head text-lg font-bold">Reportes</span></div>
          <div className="flex items-center gap-2">
            <button data-testid="report-back" onClick={() => navigate("/compras")} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><ArrowLeft size={18} /></button>
            <button data-testid="report-logout" onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {stat.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 text-center">
                <span className={`inline-grid w-9 h-9 place-items-center rounded-lg mb-2 ${s.bg} ${s.c}`}><Icon size={16} /></span>
                <p className={`font-head text-3xl font-extrabold ${s.c}`}>{s.n}</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-1">{s.l}</p>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-100"><Plus size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Reportar Problema</h3></div>
          <div className="space-y-5">
            <RField icon={Monitor} label="Plataforma">
              {plataformaOtro ? (
                <div className="flex gap-2">
                  <input data-testid="rep-plataforma-otro" value={form.plataforma} onChange={upd("plataforma")} placeholder="Escribe el servicio (cuenta de antes del cambio de web)" className={ic()} />
                  <button type="button" onClick={backFromOtro} className="shrink-0 px-3 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 text-sm font-semibold">Volver</button>
                </div>
              ) : (
                <select data-testid="rep-plataforma" value={form.plataforma} onChange={onPlataformaSelect} className={ic()}>
                  <option value="">Selecciona el servicio...</option>
                  {misPlataformas.map((name) => <option key={name} value={name}>{name}</option>)}
                  <option value="__otro__">Otro / cuenta de antes del cambio de web...</option>
                </select>
              )}
            </RField>

            <RField icon={Mail} label="Correo de la cuenta">
              <div className="relative">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="rep-correo" type="email"
                    value={form.correo_cuenta}
                    onChange={(e) => { if (compraSel) setCompraSel(null); setForm({ ...form, correo_cuenta: e.target.value }); }}
                    placeholder={form.plataforma ? "Busca el correo de esta cuenta..." : "O escribe el correo para identificarla..."}
                    className={`pl-10 ${ic()}`} />
                </div>
                {cuentaSugerencias.length > 0 && (
                  <div className="animate-dropdown absolute z-20 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-md divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {cuentaSugerencias.map((c) => (
                      <button type="button" key={c.key} onClick={() => pickCompra(c)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex justify-between gap-2 text-sm">
                        <span className="truncate">{form.plataforma ? c.correo : <><b>{c.product_name}</b> <span className="text-slate-500">— {c.correo}</span></>}{c.perfil && <span className="text-slate-400"> (Perfil {c.perfil})</span>}</span>
                        <span className={`shrink-0 flex items-center gap-1 ${c.fueraDeGarantia ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                          {c.fueraDeGarantia && <AlertTriangle size={12} />}
                          {new Date(c.fecha).toLocaleDateString("es-MX")}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {compraSel && (
                <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                  <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                  <span className="flex-1 text-emerald-800">Identificada: <b>{compraSel.product_name}{compraSel.perfil ? ` (Perfil ${compraSel.perfil})` : ""}</b>, comprada el {new Date(compraSel.fecha).toLocaleDateString("es-MX")}</span>
                  <button type="button" onClick={clearCompra} className="shrink-0 text-emerald-700 hover:text-emerald-900 font-semibold">Cambiar</button>
                </div>
              )}
              {!compraSel && !plataformaOtro && form.correo_cuenta.trim() && cuentaSugerencias.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">No encontramos esa cuenta en tu historial. Si es de antes del cambio de web, <button type="button" onClick={activarModoOtro} className="text-primary font-semibold underline">repórtala aquí</button>.</p>
              )}
              {!compraSel && !plataformaOtro && !form.correo_cuenta.trim() && form.plataforma && cuentaSugerencias.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">No tienes compras registradas de {form.plataforma}. Si es de antes del cambio de web, <button type="button" onClick={activarModoOtro} className="text-primary font-semibold underline">repórtala aquí</button>.</p>
              )}
              {compraSel?.fueraDeGarantia && (
                <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={16} className="shrink-0" />
                  <span>Esta cuenta es de 1 mes y ya pasaron {compraSel.diasPasados} días desde la compra (garantía: {WARRANTY_DAYS_MONTHLY} días) — ya no se puede reportar.</span>
                </div>
              )}
            </RField>

            <div className="grid sm:grid-cols-2 gap-4">
              <RField icon={Key} label="Contraseña de la cuenta"><input data-testid="rep-pass" value={form.password_cuenta} onChange={upd("password_cuenta")} placeholder="Contraseña" className={ic()} /></RField>
              <RField icon={Calendar} label="Fecha de compra">
                <input data-testid="rep-fecha" type="date" value={form.fecha_compra} onChange={upd("fecha_compra")} disabled={!!compraSel}
                  className={`${ic()} ${compraSel ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`} />
              </RField>
            </div>

            <RField icon={FileText} label="Descripción del problema">
              <textarea data-testid="rep-desc" value={form.descripcion} onChange={upd("descripcion")} onPaste={onDescPaste} rows={4} placeholder="Describe detalladamente el problema (mínimo 10 caracteres)" className={ic()} />
              <p className={`text-right text-xs mt-1 ${form.descripcion.trim().length >= 10 ? "text-emerald-600" : "text-slate-500"}`}>{form.descripcion.trim().length}/10 min</p>
            </RField>

            <RField icon={Camera} label={`Foto del problema ${esTop10 ? "(opcional para el Top 10)" : "(obligatorio)"}`}>
              {form.imagen_url ? (
                <div className="flex items-center gap-3">
                  <img src={form.imagen_url} alt="Foto adjunta" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => setForm((s) => ({ ...s, imagen_url: "" }))} className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"><Trash2 size={12} /> Quitar</button>
                </div>
              ) : (
                <>
                  <input ref={imgInputRef} data-testid="rep-img" type="file" accept="image/*" onChange={onImgFile} disabled={uploadingImg} className="hidden" tabIndex={-1} />
                  <button type="button" onClick={() => imgInputRef.current?.click()} disabled={uploadingImg} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary-hover disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">Seleccionar archivo</button>
                  <p className="text-xs text-slate-500 mt-1.5">O copia una captura y pégala con Ctrl+V dentro del cuadro de descripción</p>
                  {esTop10 && <p className="text-xs text-emerald-600 font-semibold mt-1.5">Estás en el Top 10 de este mes — puedes enviar el reporte sin foto si quieres.</p>}
                </>
              )}
              {uploadingImg && <p className="text-xs text-slate-500 mt-1">Subiendo imagen...</p>}
            </RField>

            <button data-testid="rep-submit" disabled={busy || uploadingImg || compraSel?.fueraDeGarantia} onClick={submit} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors"><Send size={18} /> Enviar Reporte</button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100"><ListChecks size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Mis Reportes ({counts.total})</h3></div>
          <div className="flex flex-wrap gap-2 mb-4">
            {TABS.map((t) => {
              const n = t === "Todos" ? counts.total : counts[STATUS_MAP[t]];
              return <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t === "Todos" ? "Todos" : `${t} (${n})`}</button>;
            })}
          </div>
          <div className="relative mb-4"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por correo o plataforma..." className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500" /></div>
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto text-slate-400"><ClipboardCheck size={26} /></div>
              <p className="font-head font-bold text-slate-700 mt-3">Sin reportes</p>
              <p className="text-sm text-slate-400 mt-1">Cuando reportes un problema con una cuenta, aparecerá aquí.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => {
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.pendiente;
                const StatusIcon = style.icon;
                return (
                  <div key={r.id} data-testid={`myreport-${r.id}`} className={`border border-slate-200 border-l-4 rounded-xl p-4 ${style.border}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-9 h-9 shrink-0 rounded-lg bg-primary/10 grid place-items-center text-primary"><Monitor size={16} /></span>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate">{r.plataforma || "Reporte"}{r.perfil ? ` (Perfil ${r.perfil})` : ""}</p>
                          <p className="flex items-center gap-1 text-xs text-slate-400 mt-0.5"><Clock size={11} /> {new Date(r.created_at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      </div>
                      <span className={`shrink-0 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border uppercase tracking-wide ${style.badge}`}>
                        <StatusIcon size={12} /> {r.status}
                      </span>
                    </div>

                    {(r.correo_cuenta || r.password_cuenta) && (
                      <div className="grid sm:grid-cols-2 gap-2 mt-3">
                        {r.correo_cuenta && (
                          <div className="border border-slate-200 rounded-lg px-3 py-2 min-w-0">
                            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-400"><Mail size={10} /> Correo</span>
                            <p className="text-sm text-slate-700 truncate">{r.correo_cuenta}</p>
                          </div>
                        )}
                        {r.password_cuenta && (
                          <div className="border border-slate-200 rounded-lg px-3 py-2 min-w-0">
                            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-400"><Key size={10} /> Contraseña</span>
                            <p className="text-sm text-slate-700 truncate">{r.password_cuenta}</p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="border border-slate-200 rounded-lg px-3 py-2 mt-2">
                      <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-400"><FileText size={10} /> Problema</span>
                      <p className="text-sm text-slate-700">{r.descripcion}</p>
                    </div>

                    {r.respuesta_admin && (
                      <div className="mt-3 bg-slate-900 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-400"><MessageCircle size={13} /> Respuesta del administrador</span>
                          <button onClick={() => copyText(r.respuesta_admin)} aria-label="Copiar respuesta" className="w-5 h-5 shrink-0 grid place-items-center rounded bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"><Copy size={10} /></button>
                        </div>
                        <p className="text-sm font-bold text-slate-50 whitespace-pre-wrap">{r.respuesta_admin}</p>
                        {r.respuesta_imagen && (
                          <button type="button" onClick={() => setLightboxImg(r.respuesta_imagen)} className="mt-2 block">
                            <img src={r.respuesta_imagen} alt="Adjunto de la respuesta" className="max-h-40 rounded-lg border border-slate-700 object-cover hover:opacity-90 transition-opacity" />
                          </button>
                        )}
                        {r.respondido_at && (
                          <p className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                            <Clock size={10} /> {new Date(r.respondido_at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            {r.respondido_por && ` · por ${r.respondido_por}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {lightboxImg && (
        <div role="dialog" aria-modal="true" aria-label="Foto de la respuesta" className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative max-w-md w-full">
            <button onClick={() => setLightboxImg(null)} aria-label="Cerrar" className="absolute -top-3 -right-3 w-8 h-8 grid place-items-center rounded-full bg-white text-slate-700 hover:text-slate-900 shadow-md"><X size={16} /></button>
            <img src={lightboxImg} alt="Foto de la respuesta ampliada" className="w-full max-h-[75vh] object-contain rounded-xl bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

function ic() { return "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors"; }
function RField({ icon: Icon, label, children }) {
  return <label className="block"><span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5"><Icon size={13} className="text-primary" /> {label}</span>{children}</label>;
}
